import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Github, Linkedin, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../data/mock';
import ParticleBackground from './ParticleBackground';
import AnimatedBackground from './AnimatedBackground';
import useTypingEffect from '../hooks/useTypingEffect';

const Hero = () => {
  const [showSubtitle, setShowSubtitle] = useState(false);
  const { displayedText: typedName, isComplete: nameComplete } = useTypingEffect(personalInfo.name, 80, 500);
  
  useEffect(() => {
    if (nameComplete) {
      setTimeout(() => setShowSubtitle(true), 200);
    }
  }, [nameComplete]);

  useEffect(() => {
    // Track page visit
    const trackVisit = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${BACKEND_URL}/api/analytics/visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 'home',
            referrer: document.referrer || null,
          }),
        });
      } catch (error) {
        console.log('Analytics tracking failed:', error);
      }
    };
    trackVisit();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const socialVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: 2.5 + i * 0.1,
        type: 'spring',
        stiffness: 200,
        damping: 15
      }
    }),
    hover: {
      scale: 1.2,
      rotate: 5,
      transition: { type: 'spring', stiffness: 400, damping: 10 }
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
      {/* Animated Background Elements */}
      <AnimatedBackground />
      <ParticleBackground />
      
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.05)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000,transparent)]"></div>
      
      <div className="max-w-6xl mx-auto px-6 py-20 relative z-10">
        <motion.div
          className="text-center space-y-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Greeting */}
          <motion.div variants={itemVariants} className="inline-block">
            <span className="text-emerald-400 font-medium text-lg tracking-wide">Hi there, I'm</span>
          </motion.div>
          
          {/* Name with typing effect and gradient */}
          <div className="text-6xl md:text-7xl lg:text-8xl font-bold mb-4 min-h-[100px] flex items-center justify-center">
            <h1 className="bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
              {typedName}
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.7, repeat: Infinity, repeatType: 'reverse' }}
                className="inline-block w-1 h-16 md:h-20 lg:h-24 bg-emerald-400 ml-2 align-middle"
              />
            </h1>
          </div>
          
          {/* Title with fade-in */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: showSubtitle ? 1 : 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-3"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-200">
              {personalInfo.title}
            </h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: showSubtitle ? 1 : 0, y: showSubtitle ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-xl md:text-2xl text-emerald-400 font-medium"
            >
              {personalInfo.subtitle}
            </motion.p>
          </motion.div>
          
          {/* Bio */}
          <motion.p
            variants={itemVariants}
            className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed"
          >
            {personalInfo.bio}
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap gap-4 justify-center items-center pt-6"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                onClick={() => scrollToSection('projects')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-medium rounded-lg transition-all duration-300 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40"
                aria-label="View my work section"
              >
                View My Work
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                onClick={() => window.open(personalInfo.resumeUrl, '_blank')}
                variant="outline"
                className="border-2 border-slate-700 hover:border-emerald-500 text-white px-8 py-6 text-lg font-medium rounded-lg transition-all duration-300 bg-slate-900/50 backdrop-blur-sm hover:bg-slate-800/50"
                aria-label="Download resume (opens in new tab)"
              >
                <Download className="mr-2 h-5 w-5" />
                Download Resume
              </Button>
            </motion.div>
          </motion.div>
          
          {/* Social Links with stagger animation */}
          <motion.div
            variants={itemVariants}
            className="flex gap-6 justify-center pt-8"
          >
            {[
              { href: personalInfo.github, Icon: Github, label: 'GitHub' },
              { href: personalInfo.linkedin, Icon: Linkedin, label: 'LinkedIn' },
              { href: `mailto:${personalInfo.email}`, Icon: Mail, label: 'Email' }
            ].map(({ href, Icon, label }, i) => (
              <motion.a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                custom={i}
                variants={socialVariants}
                whileHover="hover"
                className="p-3 rounded-full bg-slate-800/50 hover:bg-slate-700 border border-slate-700 hover:border-emerald-500 transition-all duration-300 backdrop-blur-sm"
                aria-label={`Visit my ${label} profile`}
              >
                <Icon className="h-6 w-6" />
              </motion.a>
            ))}
          </motion.div>
        </motion.div>
      </div>
      
      {/* Animated scroll indicator */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        role="button"
        tabIndex={0}
        onClick={() => scrollToSection('about')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scrollToSection('about');
          }
        }}
        aria-label="Scroll to about section"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2"
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1.5 h-3 bg-emerald-400 rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
